import { Window } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type BookabilityStatusItem,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { useGetTierType } from "@src/appV2/redesign/Tiers/api/useGetTierType";

import { ShiftType } from "../constants";
import { PriorityShiftPill } from "../Priority/Pill";
import { PriorityPlusShiftPill } from "../Priority/PlusPill";
import { WindowedOutPill } from "../Priority/WindowedOutPill";
import { ShiftAuctionPill } from "../ShiftAuction/Pill";
import { LimitedShiftPill } from "../Tier/LimitedShiftPill";
import { TierShiftPill } from "../Tier/Pill";
import { type WindowRestriction } from "../types";
import { UrgentShiftPill } from "../Urgent/Pill";

interface ShiftBottomSheetHeaderPillProps {
  shiftType: ShiftType;
  windowRestriction?: WindowRestriction;
  bookabilityStatus?: BookabilityStatusItem;
  hasShiftAuction: boolean;
}

/**
 * Renders pill related to the open shift
 * - Urgent
 * - Priority
 * - Priority Plus (A-Team)
 */
export function ShiftBottomSheetHeaderPill(props: ShiftBottomSheetHeaderPillProps) {
  const { shiftType, windowRestriction, bookabilityStatus, hasShiftAuction } = props;
  const { tierType, isTiersEnabled } = useGetTierType();

  const showUrgentPill = shiftType === ShiftType.URGENT;

  const showShiftAuctionPill = hasShiftAuction;

  const showTiersPill = shiftType === ShiftType.TIERS && tierType === "GOLD" && isTiersEnabled;

  const showTiersWindowedOutPill =
    isDefined(windowRestriction) &&
    windowRestriction.windowName === Window.TIERS &&
    tierType !== "GOLD" &&
    isTiersEnabled;

  const showPriorityPill =
    (shiftType === ShiftType.PRIORITY || shiftType === ShiftType.PRIORITY_LEGACY) &&
    !isTiersEnabled;

  const showLimitedTierPill =
    hasUnmetBookabilityCriteria(bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_LEAD_TIME_RESTRICTION,
      BookabilityUnmetCriteria.BASKET_SIZE_RESTRICTION,
    ]) &&
    tierType === "LIMITED" &&
    isTiersEnabled;

  const showPriorityPlusPill = shiftType === ShiftType.PRIORITY_PLUS && !isTiersEnabled;

  if (showTiersPill) {
    return <TierShiftPill variant="outlined" size="medium" />;
  }

  if (showTiersWindowedOutPill) {
    return <WindowedOutPill window={Window.TIERS} disabled={false} />;
  }

  if (showShiftAuctionPill) {
    return <ShiftAuctionPill variant="icon-highlight" size="medium" />;
  }

  if (showUrgentPill) {
    return <UrgentShiftPill variant="icon-highlight" size="medium" />;
  }

  if (showPriorityPill) {
    return <PriorityShiftPill variant="icon-highlight" size="medium" />;
  }

  if (showPriorityPlusPill) {
    return <PriorityPlusShiftPill variant="icon-highlight" size="medium" />;
  }

  if (showLimitedTierPill) {
    return <LimitedShiftPill variant="outlined" size="medium" disabled={false} />;
  }

  return null;
}
